import type { QueryClient, Query } from "@tanstack/react-query";
import axiosClient from "./apiClient";

/**
 * Remote cooktop commands and the confirm-by-state polling that follows them.
 *
 * The API only publishes an MQTT command (QoS 0) and returns immediately, so
 * a 2xx says nothing about the device having applied anything. Callers must
 * poll the sensor's reported state to know whether a command actually landed.
 */

/** "lock" re-asserts the lock on a cooktop that is off but unlocked; it uses
 *  the same endpoint as "shutoff". */
export type StoveAction = "shutoff" | "unlock" | "lock";

/** Queries the confirmation poll reads, and nothing else. Matching every
 *  query keyed to the sensor would fan out to ~13 requests per sensor per
 *  round across every target at once. `device-status` is deliberately absent:
 *  readStoveState never reads it, and its queryFn is an MQTT command/response
 *  round-trip (a GET plus up to 5s of polling), so refetching it per attempt
 *  would cost more than everything else combined. It is invalidated once when
 *  a run settles instead — see refreshLiveBadge. */
const STOVE_STATE_QUERIES = ["statechanges", "currentPCOstatechanges"];

/** The live cooktop badge's cache key prefix. */
const DEVICE_STATUS_QUERY = "device-status";

/**
 * Firmware `cts_scs_state_t` values that put the device in a power-cutoff
 * state. These are the only two the coordinator's state machine routes to
 * `power_cutoff_state()` — see the switch in
 * cts_scs_system_coordinator_task.c, where 9 falls through into 6. A remote
 * shutoff produces 9, so anything outside this pair means our command has
 * not taken effect yet.
 */
const CUTOFF_STATES = [
  6, // CTS_SCS_POWER_CUTOFF_STATE — local hazard cutoff
  9, // CTS_SCS_REMOTE_POWER_CUTOFF_STATE — remote shutoff, what we send
];

const DEFAULT_ATTEMPTS = 6;
const DEFAULT_INTERVAL_MS = 5000;

type TelemetryRow = Record<string, unknown>;
type TelemetryResponse = { data?: TelemetryRow[] };

export type StoveState = {
  /**
   * The sensor reports one of the two cutoff states. Narrower than "the
   * cooktop has no power": other states can also hold the relay open, so
   * this is a statement about the device's state machine, not about mains.
   */
  inCutoffState: boolean;
  /** The power cutoff unit reports the cooktop as remotely unlocked. */
  unlocked: boolean;
};

/** Bound on the command POST. Without one, a hung request leaves the caller's
 *  dialog waiting forever with its exits disabled. */
const COMMAND_TIMEOUT_MS = 15_000;

export const sendStoveCommand = (sensorId: string, action: StoveAction) =>
  axiosClient.post(
    `sensors/${encodeURIComponent(sensorId)}/${
      action === "unlock" ? "unlockstove" : "shutdownstove"
    }`,
    undefined,
    { timeout: COMMAND_TIMEOUT_MS }
  );

const matchesSensor = (q: Query, prefix: string, sensorId: string) =>
  Array.isArray(q.queryKey) &&
  q.queryKey[0] === prefix &&
  q.queryKey.includes(sensorId);

/**
 * Newest cached row for one telemetry query of one sensor.
 *
 * Callers key these queries differently — the sensors table embeds a page size
 * and a refresh flag, the detail page uses just the prefix and id — and the
 * table's refresh flag leaves two generations in the cache at once. Rather
 * than reconstructing a caller-specific key (which goes stale the moment the
 * flag toggles mid-poll), take whichever matching generation was updated most
 * recently. That is the freshest device data by definition, whatever the key
 * shape.
 */
function freshestRow(
  queryClient: QueryClient,
  prefix: string,
  sensorId: string
): TelemetryRow | undefined {
  const newest = queryClient
    .getQueryCache()
    .findAll({ predicate: (q) => matchesSensor(q, prefix, sensorId) })
    .reduce<Query | null>(
      (best, q) =>
        best == null || q.state.dataUpdatedAt > best.state.dataUpdatedAt
          ? q
          : best,
      null
    );
  return (newest?.state.data as TelemetryResponse | undefined)?.data?.[0];
}

/** A sensor's currently-known stove power and lock state, read from cache. */
export function readStoveState(
  queryClient: QueryClient,
  sensorId: string
): StoveState {
  const state = freshestRow(queryClient, "statechanges", sensorId);
  const pco = freshestRow(queryClient, "currentPCOstatechanges", sensorId);
  return {
    inCutoffState:
      state != null && CUTOFF_STATES.includes(state.currentStateEnum as number),
    unlocked: pco != null && !!pco.currentIsStoveRemoteUnlocked,
  };
}

/**
 * Whether a rejected command POST leaves delivery genuinely unknown.
 *
 * The server answering with a 4xx/5xx proves the command did not go out. A
 * timeout or dropped connection proves nothing: the API may well have
 * published the MQTT command before the response was lost, so the caller
 * should fall through to confirm-by-state rather than report a hard failure.
 */
export function isDeliveryUnknown(err: unknown): boolean {
  return !(err as { response?: unknown } | null)?.response;
}

/** Whether a sensor's reported state satisfies the given command. */
export function isStoveActionApplied(
  state: StoveState,
  action: StoveAction
): boolean {
  return action === "unlock"
    ? state.unlocked
    : state.inCutoffState && !state.unlocked;
}

type WaitOptions = {
  /** Called after each refetch, so a caller can rebuild views from fresh
   *  cache data. Fires only when something may have changed: on the
   *  confirming poll and once more if the device never confirms. */
  onFreshData?: () => void;
  attempts?: number;
  intervalMs?: number;
};

/**
 * Poll a sensor until it reports the state [action] should produce.
 *
 * Checks before sleeping, so a device already in the target state confirms
 * immediately. Resolves true once confirmed, or false after ~30s — false
 * means "not confirmed", not "known to have failed": the command may still
 * apply, since delivery is fire-and-forget.
 */
export async function waitForStoveState(
  queryClient: QueryClient,
  sensorId: string,
  action: StoveAction,
  { onFreshData, attempts, intervalMs }: WaitOptions = {}
): Promise<boolean> {
  const maxAttempts = attempts ?? DEFAULT_ATTEMPTS;
  const wait = intervalMs ?? DEFAULT_INTERVAL_MS;
  try {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await queryClient.refetchQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          STOVE_STATE_QUERIES.includes(q.queryKey[0] as string) &&
          q.queryKey.includes(sensorId),
      });
      if (isStoveActionApplied(readStoveState(queryClient, sensorId), action)) {
        return true;
      }
      if (attempt < maxAttempts - 1) {
        await new Promise<void>((resolve) => setTimeout(resolve, wait));
      }
    }
    return false;
  } finally {
    // Runs on the confirmed and unconfirmed paths alike: surface whatever did
    // land, so the view behind the dialog isn't left rendering pre-command
    // data, and re-probe the live badge exactly once for the whole run.
    onFreshData?.();
    void queryClient.invalidateQueries({
      queryKey: [DEVICE_STATUS_QUERY, sensorId],
    });
  }
}
